import moment from 'moment';
import type { StacAsset, StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { isTileSourceMediaType, servesTilesOn, TILE_GRID_CODE } from 'src/shared/catalogue/tileSource';
import { isGeospatialMediaType } from '../assets/assetVocabulary';
import { assembleAsset, customFieldsOf, standardFieldsOf } from '../assets/stacAssetFields';

/**
 * Turning the version form into the document the engine validates, and back.
 *
 * The rules mirrored here are the engine's own (`stac_item_rules.rs`, `source_kind.rs`),
 * checked before a round trip so a super user sees which field is wrong rather than a 422
 * about the document as a whole. The engine still has the last word — it also `HEAD`s every
 * asset, which only it can do.
 *
 * `properties.created` and `updated` are deliberately never sent: both belong to the engine,
 * which overwrites whatever arrives (ADR-016 decision 2).
 */
export type FootprintChoice = 'global' | 'regional';

export type AssetFormValues = {
    key: string;
    href: string;
    type: string;
    title: string;
    /** STAC's vocabulary for what a file is *for*. The engine reads none of it. */
    roles: string[];
    /** `proj:code`. Its own field because the engine demands it of a tile source. */
    projCode: string;
    /**
     * Extension fields the form has no input for, carried through untouched.
     *
     * Not editable here: the whole document is the escape hatch now. But they must still
     * survive, or an edit made for another reason would quietly strip them.
     */
    extensionFields: Record<string, unknown>;
};

/**
 * When a version stops being the one in force.
 *
 * Validity is derived by ordering — a version holds until the next was computed — so the
 * newest is current for ever. Closing is the only way to say a version stopped being current
 * with *nothing* replacing it: a dataset withdrawn, or a version found to be wrong and pulled.
 *
 * Ours rather than STAC's: `end_datetime` describes the span the **data** covers, which is a
 * different axis, and borrowing it would confuse the two.
 */
export const IN_FORCE_UNTIL = 'nm:in_force_until';

export type ItemFormValues = {
    id: string;
    /**
     * What to call this version, where the identifier is not what a reader wants to see.
     *
     * The identifier is fixed the moment a version is catalogued; a title can be corrected, so
     * it is the one thing here a super user can put right after the fact.
     */
    title: string;
    /** UTC. When the producer computed the data — the axis resolution runs on. */
    date: string;
    time: string;
    footprint: FootprintChoice;
    /** Carried, never edited — a narrower footprint cannot yet be stated here. */
    geometry: unknown;
    bbox?: number[];
    hasStoredRegionalFootprint: boolean;
    /** UTC day. Blank means the version is still in force, or waiting to be. */
    inForceUntil: string;
    assets: AssetFormValues[];
};

export type AssetFormErrors = Partial<Record<keyof AssetFormValues, string>>;

export type ItemFormErrors = {
    fields: Partial<Record<keyof ItemFormValues, string>>;
    /** Keyed by the asset's position, so an error stays with the row it belongs to. */
    assets: Record<number, AssetFormErrors>;
};

export const WHOLE_WORLD_BBOX = [-180, -90, 180, 90];

const wholeWorldPolygon = () => ({
    type: 'Polygon',
    coordinates: [
        [
            [-180, -90],
            [180, -90],
            [180, 90],
            [-180, 90],
            [-180, -90],
        ],
    ],
});

const isWholeWorld = (item: StacItem): boolean => JSON.stringify(item.geometry) === JSON.stringify(wholeWorldPolygon());

export const emptyAssetForm = (): AssetFormValues => ({
    key: '',
    href: '',
    type: '',
    title: '',
    roles: [],
    projCode: '',
    extensionFields: {},
});

export const emptyItemForm = (): ItemFormValues => ({
    id: '',
    title: '',
    date: '',
    time: '00:00',
    footprint: 'global',
    geometry: wholeWorldPolygon(),
    bbox: [...WHOLE_WORLD_BBOX],
    hasStoredRegionalFootprint: false,
    inForceUntil: '',
    assets: [emptyAssetForm()],
});

const isBlank = (value: string) => value.trim().length === 0;

/** `s3://bucket/key`. The engine parses the same shape and refuses anything else. */
const isReadableS3Uri = (href: string): boolean => {
    const match = /^s3:\/\/([^/\s]+)\/(.+)$/.exec(href.trim());
    return match !== null && match[2].trim().length > 0;
};

const validateAsset = (asset: AssetFormValues, index: number, all: AssetFormValues[]): AssetFormErrors => {
    const errors: AssetFormErrors = {};

    if (isBlank(asset.key)) {
        errors.key = 'A key is required.';
    } else if (all.slice(0, index).some(earlier => earlier.key.trim() === asset.key.trim())) {
        // pgSTAC stores assets as an object, so a repeated key silently drops a file.
        errors.key = 'Another asset already uses this key.';
    }

    if (isBlank(asset.href)) {
        errors.href = 'An object is required.';
    } else if (!isReadableS3Uri(asset.href)) {
        errors.href = 'Must be an s3://bucket/key object the engine can read.';
    }

    if (isBlank(asset.type)) {
        errors.type = 'A media type is required — the engine reads it to decide what it can serve.';
    }

    // Two rules, and only the first is the engine's. It demands a projection of a tile source
    // alone; we demand one of anything holding coordinates, because a file that does not say
    // what frame it is in leaves every consumer guessing.
    if (isTileSourceMediaType(asset.type)) {
        if (isBlank(asset.projCode)) {
            errors.projCode = 'A tile source must name the tile grid it was built on.';
        } else if (!servesTilesOn(asset.projCode)) {
            // The tile route refuses another matrix outright; it would draw in the wrong place.
            errors.projCode = `The tile routes serve ${TILE_GRID_CODE} only — an archive built on another grid cannot be drawn.`;
        }
    } else if (isGeospatialMediaType(asset.type) && isBlank(asset.projCode)) {
        errors.projCode = 'A file holding coordinates must say what projection they are in.';
    }

    return errors;
};

export const validateItemForm = (values: ItemFormValues): ItemFormErrors => {
    const fields: ItemFormErrors['fields'] = {};

    if (isBlank(values.id)) fields.id = 'An identifier is required.';
    if (isBlank(values.date)) fields.date = 'A computed date is required — it is the axis versions resolve on.';

    if (values.footprint === 'regional' && !values.hasStoredRegionalFootprint) {
        fields.footprint = 'Regional footprints are not supported yet — a version must cover the globe.';
    }

    if (values.assets.length === 0) {
        fields.assets = 'At least one asset is required, or there is nothing to serve.';
    }

    // A version closed before it was computed was never in force at all, which the engine
    // refuses — so the form does too, rather than trading a round trip for it.
    if (
        !isBlank(values.inForceUntil) &&
        !isBlank(values.date) &&
        moment.utc(values.inForceUntil).endOf('day').isBefore(moment.utc(values.date, 'YYYY-MM-DD'))
    ) {
        fields.inForceUntil = 'A version cannot stop being in force before it was computed.';
    }

    const assets: Record<number, AssetFormErrors> = {};
    values.assets.forEach((asset, index) => {
        const found = validateAsset(asset, index, values.assets);
        if (Object.keys(found).length > 0) {
            assets[index] = found;
        }
    });

    return { fields, assets };
};

const toAsset = (asset: AssetFormValues): StacAsset => {
    const standard = {
        href: asset.href.trim(),
        type: asset.type.trim(),
        ...(isBlank(asset.title) ? {} : { title: asset.title.trim() }),
        ...(asset.roles.length > 0 ? { roles: asset.roles } : {}),
    };

    const custom = {
        ...asset.extensionFields,
        // Its own field on the form, so it is written last and wins over a stale copy.
        ...(isBlank(asset.projCode) ? {} : { 'proj:code': asset.projCode.trim() }),
    };

    return assembleAsset(standard, custom);
};

/**
 * The link the STAC item schema makes conditional on naming a collection.
 *
 * The rule runs both ways: an item that names a `collection` must carry a link with
 * `rel: "collection"`, and one without that link must not name a collection at all. Omitting
 * it is refused, and the schema reports it as `{} is not allowed for "<id>"` — which names no
 * field and reads like nothing at all, so it is worth never producing.
 *
 * Relative, because the item is served from under its own collection.
 */
const collectionLink = (collectionId: string) => ({ rel: 'collection', href: `./${collectionId}` });

const withCollectionLink = (links: unknown[], collectionId: string): unknown[] => [
    // A link naming another dataset is repointed rather than kept beside the right one, or the
    // document would carry two answers to the same question.
    ...links.filter(link => (link as { rel?: string })?.rel !== 'collection'),
    collectionLink(collectionId),
];

export const toItemDocument = (values: ItemFormValues, collectionId: string, existing?: StacItem): StacItem => {
    const isGlobal = values.footprint === 'global';

    // Both instants are the engine's, so they are stripped rather than echoed back. The title
    // is stripped too, so clearing it drops the key instead of carrying the old one through.
    const { created: _created, updated: _updated, title: _title, ...carriedProperties } = existing?.properties ?? { datetime: null };

    return {
        ...(existing ?? {}),
        id: values.id.trim(),
        type: 'Feature',
        stac_version: existing?.stac_version ?? '1.0.0',
        collection: collectionId,
        geometry: isGlobal ? wholeWorldPolygon() : values.geometry,
        bbox: isGlobal ? [...WHOLE_WORLD_BBOX] : values.bbox,
        properties: {
            ...carriedProperties,
            // An empty title is a title, and a wrong one — it would render as a blank name
            // wherever the identifier should have shown through. Cleared means absent.
            ...(isBlank(values.title) ? {} : { title: values.title.trim() }),
            datetime: moment.utc(`${values.date} ${isBlank(values.time) ? '00:00' : values.time}`, 'YYYY-MM-DD HH:mm').toISOString(),
            // Closed at the end of the chosen day, so that whole day is still in force. Lifting
            // a closure sends `null` rather than omitting the field: the engine reads a
            // present-but-null value as "no closure", where an absent one changes nothing.
            ...(isBlank(values.inForceUntil)
                ? existing?.properties?.[IN_FORCE_UNTIL] !== undefined
                    ? { [IN_FORCE_UNTIL]: null }
                    : {}
                : { [IN_FORCE_UNTIL]: moment.utc(values.inForceUntil, 'YYYY-MM-DD').endOf('day').toISOString() }),
        },
        assets: Object.fromEntries(values.assets.map(asset => [asset.key.trim(), toAsset(asset)])),
        links: withCollectionLink(existing?.links ?? [], collectionId),
    };
};

const assetFormFrom = (key: string, asset: StacAsset): AssetFormValues => {
    const standard = standardFieldsOf(asset);
    // `proj:code` has its own input now, so it must not also appear in the free-form editor.
    const { 'proj:code': projCode, ...custom } = customFieldsOf(asset);

    return {
        key,
        href: standard.href ?? '',
        type: standard.type ?? '',
        title: standard.title ?? '',
        roles: standard.roles ?? [],
        projCode: typeof projCode === 'string' ? projCode : '',
        extensionFields: custom,
    };
};

export const formFromItem = (item: StacItem): ItemFormValues => {
    const isGlobal = isWholeWorld(item);
    const computed = item.properties.datetime ? moment.utc(item.properties.datetime) : null;

    return {
        id: item.id,
        title: typeof item.properties.title === 'string' ? item.properties.title : '',
        date: computed ? computed.format('YYYY-MM-DD') : '',
        time: computed ? computed.format('HH:mm') : '00:00',
        footprint: isGlobal ? 'global' : 'regional',
        geometry: item.geometry,
        bbox: item.bbox,
        hasStoredRegionalFootprint: !isGlobal,
        inForceUntil:
            typeof item.properties[IN_FORCE_UNTIL] === 'string'
                ? moment.utc(item.properties[IN_FORCE_UNTIL] as string).format('YYYY-MM-DD')
                : '',
        assets: Object.entries(item.assets ?? {}).map(([key, asset]) => assetFormFrom(key, asset)),
    };
};
